import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import {
  Coins,
  Plus,
  Minus,
  Clock,
  LogOut,
  Zap,
  TrendingUp,
  TrendingDown,
  History,
  Download,
  ChevronLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/useAuth';
import { api } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';

interface Transaction {
  id: string;
  type: 'credit' | 'debit';
  amount: number;
  description: string;
  tool?: string;
  balanceAfter?: number | null;
  createdAt: string;
}

export default function CreditsHistory() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [credits, setCredits] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalSpent: 0,
    totalPurchased: 0,
    thisMonth: 0
  });

  const loadCreditsData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Load current balance
      const [creditsResult, purchasesResult] = await Promise.all([
        api.getCredits(),
        api.getPurchases()
      ]);

      if (creditsResult.success && creditsResult.data) {
        setCredits(creditsResult.data.balance);
      }

      const purchases = purchasesResult.success && purchasesResult.data
        ? purchasesResult.data.purchases
        : [];

      const purchaseTransactions: Transaction[] = purchases.map((purchase) => ({
        id: `purchase-${purchase.id}`,
        type: 'credit',
        amount: purchase.credits_purchased ?? 0,
        description: purchase.pack_type
          ? t('creditsPage.creditPack', { pack: purchase.pack_type.toUpperCase() })
          : t('creditsPage.creditPurchase'),
        balanceAfter: null,
        createdAt: purchase.created_at || new Date().toISOString()
      }));

      const allTransactions = [...purchaseTransactions].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      setTransactions(allTransactions);

      const totalPurchased = purchaseTransactions.reduce((sum, t) => sum + t.amount, 0);

      setStats({
        totalSpent: 0,
        totalPurchased,
        thisMonth: 0
      });
    } catch (error) {
      console.error('Failed to load credits data:', error);
      toast({
        title: t('creditsPage.loadErrorTitle'),
        description: t('creditsPage.loadErrorBody'),
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast, t]);

  useEffect(() => {
    loadCreditsData();
  }, [loadCreditsData]);

  const exportHistory = () => {
    const csv = [
      [t('creditsPage.csvDate'), t('creditsPage.csvType'), t('creditsPage.csvAmount'), t('creditsPage.csvDescription'), t('creditsPage.csvBalanceAfter')].join(','),
      ...transactions.map(t => [
        new Date(t.createdAt).toLocaleDateString(),
        t.type,
        t.amount,
        t.description,
        t.balanceAfter
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `credits-history-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-card/80 backdrop-blur-xl border-b border-border">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <Zap className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-bold font-display">
              <span className="gradient-text">Chop</span>Shop
            </span>
          </Link>

          <div className="flex items-center gap-2 sm:gap-4">
            <div className="flex items-center gap-2 rounded-xl bg-muted px-3 py-2 sm:px-4">
              <Coins className="w-4 h-4 text-primary" />
              <span className="font-semibold">{credits.toLocaleString()}</span>
              <span className="text-muted-foreground text-sm hidden sm:inline">{t('creditsPage.credits')}</span>
            </div>

            <Link to="/pricing">
              <Button variant="glow" size="sm">
                <Plus className="w-4 h-4 mr-1" />
                {t('creditsPage.buyCredits')}
              </Button>
            </Link>

            <button
              onClick={() => {
                logout();
                navigate('/');
              }}
              className="p-2 rounded-lg hover:bg-muted transition-colors"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12">
        <div className="container mx-auto px-4 max-w-6xl">
          {/* Back Button */}
          <Link to="/chopshop">
            <Button variant="ghost" className="mb-6">
              <ChevronLeft className="w-4 h-4 mr-2" />
              {t('creditsPage.backToChopShop')}
            </Button>
          </Link>

          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
              <History className="w-10 h-10 text-primary" />
              {t('creditsPage.pageTitle')}
            </h1>
            <p className="text-muted-foreground text-lg">{t('creditsPage.pageSubtitle')}</p>
          </motion.div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{t('creditsPage.currentBalance')}</CardTitle>
                  <Coins className="w-4 h-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{credits.toLocaleString()}</div>
                  <p className="text-xs text-muted-foreground mt-1">{t('creditsPage.availableCredits')}</p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{t('creditsPage.thisMonth')}</CardTitle>
                  <TrendingDown className="w-4 h-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.thisMonth.toLocaleString()}</div>
                  <p className="text-xs text-muted-foreground mt-1">{t('creditsPage.creditsUsed')}</p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{t('creditsPage.allTime')}</CardTitle>
                  <TrendingUp className="w-4 h-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.totalPurchased.toLocaleString()}</div>
                  <p className="text-xs text-muted-foreground mt-1">{t('creditsPage.creditsPurchased')}</p>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Transactions List */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>{t('creditsPage.transactionHistory')}</CardTitle>
                  <CardDescription>{t('creditsPage.transactionHistoryNote')}</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={exportHistory}>
                  <Download className="w-4 h-4 mr-2" />
                  {t('creditsPage.exportCsv')}
                </Button>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="text-center py-12 text-muted-foreground">
                    {t('creditsPage.loadingTransactions')}
                  </div>
                ) : transactions.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    {t('creditsPage.noTransactions')}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {transactions.map((transaction) => (
                      <div
                        key={transaction.id}
                        className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                      >
                        <div className="flex min-w-0 flex-1 items-center gap-4">
                          <div className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center ${transaction.type === 'credit'
                            ? 'bg-green-500/20'
                            : 'bg-red-500/20'
                            }`}>
                            {transaction.type === 'credit' ? (
                              <Plus className="w-5 h-5 text-green-500" />
                            ) : (
                              <Minus className="w-5 h-5 text-red-500" />
                            )}
                          </div>
                          <div>
                            <div className="break-words font-medium">{transaction.description}</div>
                            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mt-1">
                              <Clock className="w-3 h-3" />
                              {new Date(transaction.createdAt).toLocaleString()}
                              {transaction.tool && (
                                <span className="px-2 py-0.5 rounded bg-primary/10 text-primary">
                                  {transaction.tool}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`font-semibold ${transaction.type === 'credit' ? 'text-green-500' : 'text-red-500'
                            }`}>
                            {transaction.type === 'credit' ? '+' : '-'}{transaction.amount.toLocaleString()}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {t('creditsPage.balanceAfter', { balance: transaction.balanceAfter.toLocaleString() })}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
